import { useCallback } from 'react';

import {
  useAllZoomRecordingsQuery,
  AllZoomRecordingsQuery,
  AllZoomRecordingsQueryVariables,
} from 'core/graphql';

type ZoomRecordingFilters = Omit<
  AllZoomRecordingsQueryVariables,
  'first' | 'after'
>;

/**
 * The recordings-list node — the shared `ZoomRecordingFields` fragment PLUS the
 * inline `lessonRecordings` selection (video → lesson nav). Derived from the
 * query type so the nav link stays typed.
 */
export type ZoomRecordingRow = NonNullable<
  NonNullable<
    NonNullable<AllZoomRecordingsQuery['allZoomRecordings']>['edges'][number]
  >['node']
>;

interface IZoomRecordingsHook {
  data: ZoomRecordingRow[];
  loading: boolean;
  error?: string;
  hasNextPage: boolean;
  operations: {
    fetchMore: () => void;
    refetch: () => void;
  };
}

/** Relay-cursor paginated list of raw ZoomRecording rows (incl. orphans). */
export const useZoomRecordings = (
  filters: ZoomRecordingFilters = {},
  pageSize = 50,
): IZoomRecordingsHook => {
  const {
    data,
    loading,
    error,
    fetchMore,
    refetch,
  } = useAllZoomRecordingsQuery({
    variables: { ...filters, first: pageSize },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const connection = data?.allZoomRecordings;
  const edges = connection?.edges ?? [];

  const handleFetchMore = useCallback(() => {
    if (!connection?.pageInfo?.hasNextPage) {
      return;
    }

    fetchMore({
      variables: {
        ...filters,
        first: pageSize,
        after: connection.pageInfo.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult?.allZoomRecordings) {
          return prev;
        }

        return {
          allZoomRecordings: {
            ...fetchMoreResult.allZoomRecordings,
            edges: [
              ...(prev.allZoomRecordings?.edges ?? []),
              ...fetchMoreResult.allZoomRecordings.edges,
            ],
          },
        };
      },
    });
  }, [connection, fetchMore, filters, pageSize]);

  return {
    data: edges
      .map((edge) => edge?.node)
      .filter((node): node is ZoomRecordingRow => Boolean(node)),
    loading,
    error: error?.message,
    hasNextPage: Boolean(connection?.pageInfo?.hasNextPage),
    operations: {
      fetchMore: handleFetchMore,
      refetch: () => {
        refetch();
      },
    },
  };
};
